"use client"

import { useState, useEffect } from "react"
import { useAuth0 } from "@auth0/auth0-react"
import { getApiBaseUrl } from "../config/apiBase"

const useMaterialsResources = (actionId, action) => {
  console.log("useMaterialsResources hook initialized with actionId:", actionId)

  const [materials, setMaterials] = useState([])
  const [suggestions, setSuggestions] = useState([])
  const [comments, setComments] = useState([])
  const [newSuggestion, setNewSuggestion] = useState([{ name: "", quantity: "", type: "material", notes: "" }])
  const [newComment, setNewComment] = useState("")
  const [activeTab, setActiveTab] = useState("current")
  const [loading, setLoading] = useState(true)

  const { getAccessTokenSilently } = useAuth0()
  const apiUrl = getApiBaseUrl()
  console.log("API URL:", apiUrl)

  const fetchMaterialsData = async () => {
    console.log("fetchMaterialsData called with actionId:", actionId)

    if (!actionId) {
      console.log("No actionId provided, skipping fetch")
      setLoading(false)
      return
    }

    try {
      setLoading(true)
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      console.log("Token exists:", !!token)

      // Fetch the current materials
      const materialsUrl = `${apiUrl}/api/actions/${actionId}/refinement/modules/materials`
      console.log("Fetching materials from:", materialsUrl)

      const materialsResponse = await fetch(materialsUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log("Materials response status:", materialsResponse.status)

      if (materialsResponse.ok) {
        const materialsData = await materialsResponse.json()
        console.log("Materials data received:", materialsData)

        // Ensure we always set an array
        if (Array.isArray(materialsData)) {
          setMaterials(materialsData)
        } else if (materialsData && typeof materialsData === "object") {
          if (Array.isArray(materialsData.materials)) {
            setMaterials(materialsData.materials)
          } else if (Array.isArray(materialsData.data)) {
            setMaterials(materialsData.data)
          } else {
            console.warn("Materials data is not in expected format:", materialsData)
            setMaterials([])
          }
        } else {
          setMaterials([])
        }
      } else {
        console.error("Materials response not ok:", materialsResponse.status, materialsResponse.statusText)
        setMaterials([])
      }

      // Fetch suggestions
      const suggestionsUrl = `${apiUrl}/api/actions/${actionId}/refinement/modules/materials/suggestions`
      console.log("Fetching suggestions from:", suggestionsUrl)

      const suggestionsResponse = await fetch(suggestionsUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log("Suggestions response status:", suggestionsResponse.status)

      if (suggestionsResponse.ok) {
        const suggestionsData = await suggestionsResponse.json()
        console.log("Suggestions data received:", suggestionsData)

        // Ensure we always set an array
        if (Array.isArray(suggestionsData)) {
          setSuggestions(suggestionsData)
        } else if (suggestionsData && typeof suggestionsData === "object") {
          if (Array.isArray(suggestionsData.suggestions)) {
            setSuggestions(suggestionsData.suggestions)
          } else if (Array.isArray(suggestionsData.data)) {
            setSuggestions(suggestionsData.data)
          } else {
            setSuggestions([])
          }
        } else {
          setSuggestions([])
        }
      } else {
        console.error("Suggestions response not ok:", suggestionsResponse.status, suggestionsResponse.statusText)
        setSuggestions([])
      }

      // Fetch comments
      const commentsUrl = `${apiUrl}/api/actions/${actionId}/refinement/modules/materials/comments`
      console.log("Fetching comments from:", commentsUrl)

      const commentsResponse = await fetch(commentsUrl, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      console.log("Comments response status:", commentsResponse.status)

      if (commentsResponse.ok) {
        const commentsData = await commentsResponse.json()
        console.log("Comments data received:", commentsData)

        // Ensure we always set an array
        if (Array.isArray(commentsData)) {
          setComments(commentsData)
        } else if (commentsData && typeof commentsData === "object") {
          if (Array.isArray(commentsData.comments)) {
            setComments(commentsData.comments)
          } else if (Array.isArray(commentsData.data)) {
            setComments(commentsData.data)
          } else {
            setComments([])
          }
        } else {
          setComments([])
        }
      } else {
        console.error("Comments response not ok:", commentsResponse.status, commentsResponse.statusText)
        setComments([])
      }
    } catch (error) {
      console.error("Error fetching materials data:", error)
      // Ensure we set empty arrays on error
      setMaterials([])
      setSuggestions([])
      setComments([])
    } finally {
      setLoading(false)
    }
  }

  const handleAddMaterial = () => {
    setNewSuggestion([...newSuggestion, { name: "", quantity: "", type: "material", notes: "" }])
  }

  const handleRemoveMaterial = (index) => {
    if (newSuggestion.length > 1) {
      setNewSuggestion(newSuggestion.filter((_, i) => i !== index))
    }
  }

  const handleMaterialChange = (index, field, value) => {
    const updatedMaterials = [...newSuggestion]
    updatedMaterials[index][field] = value
    setNewSuggestion(updatedMaterials)
  }

  const handleSubmitSuggestion = async () => {
    console.log("handleSubmitSuggestion called with:", newSuggestion)

    // Validate that at least one material has a name
    if (!newSuggestion.some((item) => item.name.trim())) {
      console.log("No valid materials provided, skipping submit")
      return
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const url = `${apiUrl}/api/actions/${actionId}/refinement/modules/materials/suggestions`

      console.log("Submitting suggestion to:", url)
      console.log("Suggestion data:", { materials: newSuggestion })

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: JSON.stringify({ materials: newSuggestion }) }),
      })

      console.log("Submit suggestion response status:", response.status)

      if (response.ok) {
        setNewSuggestion([{ name: "", quantity: "", type: "material", notes: "" }])
        await fetchMaterialsData()
      } else {
        console.error("Failed to submit suggestion:", response.status, response.statusText)
      }
    } catch (error) {
      console.error("Error submitting suggestion:", error)
    }
  }

  const handleSubmitComment = async () => {
    console.log("handleSubmitComment called with:", newComment)

    if (!newComment.trim()) {
      console.log("Empty comment, skipping submit")
      return
    }

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const url = `${apiUrl}/api/actions/${actionId}/refinement/modules/materials/comments`

      console.log("Submitting comment to:", url)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ content: newComment }),
      })

      console.log("Submit comment response status:", response.status)

      if (response.ok) {
        setNewComment("")
        await fetchMaterialsData()
      } else {
        console.error("Failed to submit comment:", response.status, response.statusText)
      }
    } catch (error) {
      console.error("Error submitting comment:", error)
    }
  }

  const handleVote = async (suggestionId, voteType) => {
    console.log("handleVote called with:", suggestionId, voteType)

    try {
      const token = await getAccessTokenSilently({
        authorizationParams: {
          audience: process.env.REACT_APP_API_AUDIENCE,
        },
      })
      const url = `${apiUrl}/api/actions/${actionId}/refinement/suggestions/${suggestionId}/vote`

      console.log("Submitting vote to:", url)

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ voteType }),
      })

      console.log("Vote response status:", response.status)

      if (response.ok) {
        await fetchMaterialsData()
      } else {
        console.error("Failed to submit vote:", response.status, response.statusText)
      }
    } catch (error) {
      console.error("Error submitting vote:", error)
    }
  }

  useEffect(() => {
    console.log("useEffect triggered with actionId:", actionId)
    fetchMaterialsData()
  }, [actionId])

  console.log("useMaterialsResources hook returning state:", {
    materialsCount: Array.isArray(materials) ? materials.length : "NOT_ARRAY",
    suggestionsCount: Array.isArray(suggestions) ? suggestions.length : "NOT_ARRAY",
    commentsCount: Array.isArray(comments) ? comments.length : "NOT_ARRAY",
    loading,
  })

  return {
    // State
    materials: Array.isArray(materials) ? materials : [],
    suggestions: Array.isArray(suggestions) ? suggestions : [],
    comments: Array.isArray(comments) ? comments : [],
    newSuggestion,
    newComment,
    activeTab,
    loading,

    // Actions
    setActiveTab,
    setNewComment,
    handleAddMaterial,
    handleRemoveMaterial,
    handleMaterialChange,
    handleSubmitSuggestion,
    handleSubmitComment,
    handleVote,
    fetchMaterialsData,
  }
}

export default useMaterialsResources
